import { DOMAINS, type Domain } from "../../domain/contracts";
import { getIslandResourceDomain } from "./islandCachePolicy";

const MEBIBYTE_BYTES = 1024 * 1024;
const RESOURCE_URL_BASE = "https://little-launch-island.invalid";

export const OFFLINE_RESOURCE_BUDGET_BYTES = {
  core: 20 * MEBIBYTE_BYTES,
  island: 15 * MEBIBYTE_BYTES,
  runtime: 100 * MEBIBYTE_BYTES
} as const;

export interface RuntimeResourceByteSize {
  readonly path: string;
  readonly byteLength: number;
}

export interface OfflineResourceBudgetUsage {
  readonly resourceCount: number;
  readonly totalBytes: number;
  readonly limitBytes: number;
  readonly remainingBytes: number;
  readonly overageBytes: number;
  readonly isWithinBudget: boolean;
}

export interface OfflineResourceBudgetReport {
  readonly core: OfflineResourceBudgetUsage;
  readonly islands: ReadonlyMap<Domain, OfflineResourceBudgetUsage>;
  readonly runtime: OfflineResourceBudgetUsage;
}

export function createOfflineResourceBudgetReport(
  resources: readonly RuntimeResourceByteSize[]
): OfflineResourceBudgetReport {
  const inventory = new Map<string, number>();

  for (const resource of resources) {
    validateByteLength(resource);
    const normalizedPath = normalizeResourcePath(resource.path);
    const existingByteLength = inventory.get(normalizedPath);

    if (
      existingByteLength !== undefined &&
      existingByteLength !== resource.byteLength
    ) {
      throw new Error(
        "Conflicting byte lengths for runtime resource " + normalizedPath
      );
    }
    inventory.set(normalizedPath, resource.byteLength);
  }

  const coreResources = new Map<string, number>();
  const islandResources = new Map<Domain, Map<string, number>>(
    DOMAINS.map(
      (domain): readonly [Domain, Map<string, number>] => [
        domain,
        new Map<string, number>()
      ]
    )
  );

  for (const [resourcePath, byteLength] of inventory) {
    const domain = getIslandResourceDomain(resourcePath);
    if (domain === null) {
      coreResources.set(resourcePath, byteLength);
    } else {
      readIslandResources(islandResources, domain).set(
        resourcePath,
        byteLength
      );
    }
  }

  return {
    core: createBudgetUsage(
      coreResources,
      OFFLINE_RESOURCE_BUDGET_BYTES.core
    ),
    islands: new Map(
      DOMAINS.map(
        (domain): readonly [Domain, OfflineResourceBudgetUsage] => [
          domain,
          createBudgetUsage(
            readIslandResources(islandResources, domain),
            OFFLINE_RESOURCE_BUDGET_BYTES.island
          )
        ]
      )
    ),
    runtime: createBudgetUsage(
      inventory,
      OFFLINE_RESOURCE_BUDGET_BYTES.runtime
    )
  };
}

export function assertOfflineResourceBudgets(
  report: OfflineResourceBudgetReport
): void {
  const violations = budgetEntries(report).filter(
    ([, usage]) => !usage.isWithinBudget
  );

  if (violations.length === 0) {
    return;
  }

  throw new Error(
    "Offline resource budget exceeded:\n" +
      violations
        .map(
          ([name, usage]) =>
            name + " by " + formatMebibytes(usage.overageBytes) + " MiB"
        )
        .join("\n") +
      "\n\n" +
      formatOfflineResourceBudgetReport(report)
  );
}

export function formatOfflineResourceBudgetReport(
  report: OfflineResourceBudgetReport
): string {
  return budgetEntries(report)
    .map(
      ([name, usage]) =>
        name +
        ": " +
        formatMebibytes(usage.totalBytes) +
        " / " +
        formatMebibytes(usage.limitBytes) +
        " MiB (" +
        (usage.isWithinBudget ? "within budget" : "over budget") +
        ")"
    )
    .join("\n");
}

function budgetEntries(
  report: OfflineResourceBudgetReport
): Array<readonly [string, OfflineResourceBudgetUsage]> {
  const entries: Array<readonly [string, OfflineResourceBudgetUsage]> = [
    ["core", report.core]
  ];

  for (const domain of DOMAINS) {
    entries.push([domain, readIslandUsage(report.islands, domain)]);
  }
  entries.push(["runtime", report.runtime]);
  return entries;
}

function createBudgetUsage(
  resources: ReadonlyMap<string, number>,
  limitBytes: number
): OfflineResourceBudgetUsage {
  let totalBytes = 0;
  for (const byteLength of resources.values()) {
    totalBytes += byteLength;
  }

  return {
    resourceCount: resources.size,
    totalBytes,
    limitBytes,
    remainingBytes: Math.max(0, limitBytes - totalBytes),
    overageBytes: Math.max(0, totalBytes - limitBytes),
    isWithinBudget: totalBytes <= limitBytes
  };
}

function validateByteLength(resource: RuntimeResourceByteSize): void {
  if (
    !Number.isSafeInteger(resource.byteLength) ||
    resource.byteLength < 0
  ) {
    throw new RangeError(
      "Invalid byte length for runtime resource " + resource.path
    );
  }
}

function normalizeResourcePath(resourcePath: string): string {
  try {
    return new URL(resourcePath, RESOURCE_URL_BASE).pathname;
  } catch {
    throw new Error("Invalid runtime resource path " + resourcePath);
  }
}

function readIslandResources(
  resources: ReadonlyMap<Domain, Map<string, number>>,
  domain: Domain
): Map<string, number> {
  const selected = resources.get(domain);
  if (selected === undefined) {
    throw new Error("Missing resource budget group for " + domain);
  }
  return selected;
}

function readIslandUsage(
  usages: ReadonlyMap<Domain, OfflineResourceBudgetUsage>,
  domain: Domain
): OfflineResourceBudgetUsage {
  const usage = usages.get(domain);
  if (usage === undefined) {
    throw new Error("Missing resource budget usage for " + domain);
  }
  return usage;
}

function formatMebibytes(bytes: number): string {
  return (bytes / MEBIBYTE_BYTES).toFixed(2);
}
